import React, { useState, useCallback, useMemo, useRef, useEffect, memo } from "react";
import { COLORS } from "../config/themes";
import Icon from "../components/Icon";
import { studentName, todayStr } from "../utils/helpers";
import { ACTIVITY_TYPES } from "../config/constants";
import { insertActivityEntry, upsertMessage, upsertChannel } from "../utils/db";
import { supabase } from "../supabaseClient";
import MyAttendancePage from "./MyAttendancePage";
import EventsPage from "./EventsPage";
import TicketsPage from "./TicketsPage";
import StudentsPage from "./StudentsPage";
import ReportsPage from "./ReportsPage";
import ClassroomsPage from "./ClassroomsPage";
import AttendancePage from "./AttendancePage";


// ─── Portal dark palette (matches mockup — always dark regardless of app theme) ──
// P palette — reads from COLORS at render time so all theme changes (light/dark/season) apply
const P_DARK = {
  bg:          "#1A1A1A",
  card:        "#242424",
  cardAlt:     "#2C2C2C",
  border:      "rgba(255,255,255,0.1)",
  borderLight: "rgba(255,255,255,0.06)",
  text:        "#F0F0F0",
  textMuted:   "#909090",
  textDim:     "#606060",
  navy:        "#F0F0F0",
};
// P proxies ALL keys from COLORS so the portal fully respects the selected theme
const P = new Proxy({}, {
  get(_, key) {
    const isDark = COLORS.bg && (
      COLORS.bg === "#1A1A1A" || COLORS.bg === "#1A1218" || COLORS.bg === "#1A1410" ||
      COLORS.bg === "#1A1308" || COLORS.bg === "#0C1220" || COLORS.bg < "#888"
    );
    // In dark mode use our hardcoded dark palette for bg/card/border/text
    // In light mode use COLORS directly so the theme applies fully
    if (!isDark) {
      // Light mode — use COLORS for everything
      if (key === "bg")          return COLORS.bg || "#F9F9F9";
      if (key === "card")        return COLORS.card || "#FFFFFF";
      if (key === "cardAlt")     return COLORS.borderLight || "#F5F5F5";
      if (key === "border")      return COLORS.border || "rgba(0,0,0,0.1)";
      if (key === "borderLight") return COLORS.borderLight || "rgba(0,0,0,0.06)";
      if (key === "text")        return COLORS.text || "#1A1A1A";
      if (key === "textMuted")   return COLORS.textMuted || "#666666";
      if (key === "textDim")     return COLORS.textMuted || "#999999";
      if (key === "navy")        return COLORS.navy || "#1A1A1A";
    } else {
      // Dark mode — use hardcoded dark palette for structure
      if (key === "bg")          return P_DARK.bg;
      if (key === "card")        return P_DARK.card;
      if (key === "cardAlt")     return P_DARK.cardAlt;
      if (key === "border")      return P_DARK.border;
      if (key === "borderLight") return P_DARK.borderLight;
      if (key === "text")        return P_DARK.text;
      if (key === "textMuted")   return P_DARK.textMuted;
      if (key === "textDim")     return P_DARK.textDim;
      if (key === "navy")        return P_DARK.navy;
    }
    // Semantic colors always from COLORS (theme-aware for both modes)
    if (key === "primary")     return COLORS.primary || "#E8679A";
    if (key === "primaryPale") return (COLORS.primary || "#E8679A") + "28";
    if (key === "accent")      return COLORS.accent || "#4CAF7D";
    if (key === "accentPale")  return COLORS.accentPale || "rgba(76,175,125,0.18)";
    if (key === "warning")     return COLORS.warning || "#F5A623";
    if (key === "warningPale") return COLORS.warningPale || "rgba(245,166,35,0.18)";
    if (key === "danger")      return COLORS.danger || "#EF5350";
    if (key === "dangerPale")  return COLORS.dangerPale || "rgba(239,83,80,0.18)";
    return COLORS[key] || "";
  }
});

// ─── Read tracking via localStorage (survives polls + page switches) ────────────
const getLastRead = (userId, channelId) => {
  try { return localStorage.getItem(`lr_${userId}_${channelId}`) || ""; } catch { return ""; }
};
const setLastRead = (userId, channelId) => {
  try { localStorage.setItem(`lr_${userId}_${channelId}`, new Date().toISOString()); } catch {}
};

// ─── constants ────────────────────────────────────────────────────────────────
const HIT = 48;
const MEAL_OPTS     = ["Breakfast","AM Snack","Lunch","PM Snack","Bottle"];
const ACTIVITY_OPTS = ["Outdoor Play","Circle Time","Free Play","Art","Reading","Music","Indoor Play","Group Activity"];
const NAP_OPTS      = ["Fell asleep","Woke up","Resting"];
const MOOD_OPTS     = ["Happy","Calm","Fussy","Tired","Excited","Shy"];
const DIAPER_OPTS   = ["Wet","BM","Dry","Potty"];
const MED_OPTS      = ["As prescribed","Sunscreen applied","First aid"];
const MILESTONE_OPTS= ["First steps","New word","Shared with friend","Learned new skill","Self-care achievement"];

const LOG_TILES_PRIMARY = [
  { id:"meal",      label:"Meal",     iconName:"meal",      color:"#E8852E" },
  { id:"nap",       label:"Nap",      iconName:"nap",       color:"#7C4DFF" },
  { id:"mood",      label:"Mood",     iconName:"mood",      color:"#E91E63" },
  { id:"photo",     label:"Photo",    iconName:"camera",    color:"#378ADD" },
];
const LOG_TILES_MORE = [
  { id:"note",      label:"Note",      iconName:"note",      color:"#8B7E6A" },
  { id:"diaper",    label:"Diaper",    iconName:"diaper",    color:"#00BCD4" },
  { id:"medicine",  label:"Medicine",  iconName:"medicine",  color:"#E74C3C" },
  { id:"milestone", label:"Milestone", iconName:"milestone", color:"#F39C12" },
  { id:"learning",  label:"Learning",  iconName:"learning",  color:"#3498DB" },
];

function detailOpts(type) {
  if (type==="meal")                        return MEAL_OPTS;
  if (type==="activity"||type==="learning") return ACTIVITY_OPTS;
  if (type==="nap")                         return NAP_OPTS;
  if (type==="mood")                        return MOOD_OPTS;
  if (type==="diaper")                      return DIAPER_OPTS;
  if (type==="medicine")                    return MED_OPTS;
  if (type==="milestone")                   return MILESTONE_OPTS;
  return [];
}

const cardStyle = () => ({
  background: P.card,
  border: `1px solid ${P.border}`,
  borderRadius: 16,
  overflow: "hidden",
});

// ─── Shared UI ────────────────────────────────────────────────────────────────
const AvatarInitials = ({ name, size=40, photo }) => {
  const parts = (name||"?").trim().split(" ");
  const ini = parts.length>=2 ? parts[0][0]+parts[parts.length-1][0] : parts[0].slice(0,2);
  if (photo) return <img src={photo} alt={name} style={{width:size,height:size,borderRadius:size/2,objectFit:"cover",flexShrink:0}}/>;
  return (
    <div style={{width:size,height:size,borderRadius:size/2,background:P.primaryPale,
      display:"flex",alignItems:"center",justifyContent:"center",
      fontSize:size*0.36,fontWeight:700,color:P.primary,flexShrink:0,letterSpacing:-0.5}}>
      {ini.toUpperCase()}
    </div>
  );
};

const Chip = ({ label, selected, onPress, color }) => {
  const c = color || P.primary;
  return (
    <button onClick={onPress} style={{
      height:36,padding:"0 16px",borderRadius:18,
      border:`1.5px solid ${selected?c:P.border}`,
      background:selected?c+"22":"transparent",
      color:selected?c:P.textMuted,
      fontSize:13,fontWeight:selected?700:400,
      cursor:"pointer",whiteSpace:"nowrap",fontFamily:"inherit",flexShrink:0,
    }}>{label}</button>
  );
};

// ─── Nav icon SVGs ────────────────────────────────────────────────────────────
const NavIcon = ({ id, color }) => {
  const p = { width:22, height:22, viewBox:"0 0 24 24", fill:"none", stroke:color, strokeWidth:"1.8", strokeLinecap:"round", strokeLinejoin:"round" };
  if (id==="today")    return <svg {...p}><rect x="3" y="4" width="18" height="18" rx="3"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
  if (id==="log")      return <svg {...p}><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>;
  if (id==="messages") return <svg {...p}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>;
  if (id==="more")     return <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="5" cy="12" r="1.8" fill={color}/><circle cx="12" cy="12" r="1.8" fill={color}/><circle cx="19" cy="12" r="1.8" fill={color}/></svg>;
  return null;
};

// ─── TODAY ────────────────────────────────────────────────────────────────────
const TodayTab = memo(({ students, classroom, studentAttendance, setStudentAttendance, activityLog, today, onOpenLog, schoolId, isDemo, currentUser }) => {
  const dayAtt = studentAttendance[today] || {};
  const active = students.filter(s => !s.enrollmentDate || s.enrollmentDate <= today);

  const counts = useMemo(() => {
    const present  = active.filter(s => dayAtt[s.id]==="present").length;
    const late     = active.filter(s => dayAtt[s.id]==="late").length;
    const absent   = active.filter(s => dayAtt[s.id]==="absent"||(dayAtt[s.id]&&dayAtt[s.id].startsWith("absent:"))).length;
    return { present, late, absent, unmarked: active.length - present - late - absent };
  }, [active, dayAtt]);

  const setStatus = useCallback(async (sid, status) => {
    setStudentAttendance(prev => ({ ...prev, [today]: { ...(prev[today]||{}), [sid]: status } }));
    if (!isDemo && schoolId) {
      await supabase.from("student_attendance")
        .upsert({ school_id:schoolId, student_id:sid, date:today, status }, { onConflict:"school_id,student_id,date" })
        .catch(()=>{});
    }
  }, [today, setStudentAttendance, schoolId, isDemo]);

  const checkInAll = () => active.filter(s => !dayAtt[s.id]).forEach(s => setStatus(s.id,"present"));

  const unmarked = active.filter(s => !dayAtt[s.id]);
  const present  = active.filter(s => dayAtt[s.id]==="present"||dayAtt[s.id]==="late");
  const absent   = active.filter(s => dayAtt[s.id]==="absent"||(dayAtt[s.id]&&dayAtt[s.id].startsWith("absent:")));
  const logCount = sid => (activityLog[today]||[]).filter(e => e.studentId===sid).length;

  return (
    <div style={{ paddingBottom:80 }}>

      {/* Header */}
      <div style={{ padding:"22px 20px 18px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
          <div>
            <div style={{ fontSize:26, fontWeight:800, color:P.navy, fontFamily:"'DM Serif Display',serif", lineHeight:1.1 }}>
              {classroom?.name || "My classroom"}
            </div>
            <div style={{ fontSize:13, color:P.textMuted, marginTop:5 }}>
              {new Date().toLocaleDateString("en-US",{ weekday:"long", month:"long", day:"numeric" })}
            </div>
          </div>
          <div style={{ background:P.accentPale, color:P.accent, fontSize:12, fontWeight:700, padding:"6px 14px", borderRadius:20, marginTop:2 }}>
            {new Date().toLocaleTimeString("en-US",{ hour:"numeric", minute:"2-digit" })}
          </div>
        </div>

        {/* Ratio strip */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
          {[
            { label:"Present",  val:counts.present,  color:P.accent,    bg:P.accentPale   },
            { label:"Late",     val:counts.late,     color:P.warning,   bg:P.warningPale  },
            { label:"Absent",   val:counts.absent,   color:P.danger,    bg:P.dangerPale   },
            { label:"Expected", val:counts.unmarked, color:P.textMuted, bg:P.borderLight  },
          ].map(({ label, val, color, bg }) => (
            <div key={label} style={{ background:bg, borderRadius:14, padding:"14px 8px", textAlign:"center" }}>
              <div style={{ fontSize:28, fontWeight:800, color, lineHeight:1, fontFamily:"'DM Serif Display',serif" }}>{val}</div>
              <div style={{ fontSize:10, fontWeight:600, color:P.textMuted, marginTop:4, textTransform:"uppercase", letterSpacing:0.5 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Check-in-all */}
      {unmarked.length > 0 && (
        <button onClick={checkInAll} style={{
          width:"100%", display:"flex", alignItems:"center", gap:14, padding:"15px 20px",
          background:P.primary+"16", border:"none",
          borderTop:`1px solid ${P.primary}20`, borderBottom:`1px solid ${P.primary}20`,
          cursor:"pointer", fontFamily:"inherit",
        }}>
          <div style={{ width:36, height:36, borderRadius:18, background:P.primary, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <div style={{ flex:1, textAlign:"left" }}>
            <div style={{ fontSize:14, fontWeight:700, color:P.primary }}>Check in all {unmarked.length} remaining</div>
            <div style={{ fontSize:12, color:P.textMuted, marginTop:1 }}>Tap to mark everyone present at once</div>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={P.primary} strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      )}

      {/* Unmarked */}
      {unmarked.length > 0 && (
        <div style={{ padding:"20px 20px 0" }}>
          <div style={{ fontSize:11, fontWeight:700, color:P.textMuted, textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>
            Not checked in · {unmarked.length}
          </div>
          <div style={cardStyle()}>
            {unmarked.map((s, i) => (
              <div key={s.id} style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 16px", borderBottom:i<unmarked.length-1?`1px solid ${P.borderLight}`:"none" }}>
                <AvatarInitials name={studentName(s)} size={42} photo={s.photo}/>
                <div style={{ flex:1, fontSize:15, fontWeight:600, color:P.text }}>{studentName(s)}</div>
                <div style={{ display:"flex", gap:8 }}>
                  {[["In","present",P.accent],["Late","late",P.warning],["Out","absent",P.danger]].map(([lbl,st,c]) => (
                    <button key={lbl} onClick={() => setStatus(s.id,st)} style={{
                      height:38, padding:"0 14px", borderRadius:19,
                      border:`1.5px solid ${c}55`, background:`${c}18`,
                      fontSize:13, fontWeight:700, color:c, cursor:"pointer", fontFamily:"inherit",
                    }}>{lbl}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Present */}
      {present.length > 0 && (
        <div style={{ padding:"20px 20px 0" }}>
          <div style={{ fontSize:11, fontWeight:700, color:P.textMuted, textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>
            In room · {present.length}
          </div>
          <div style={cardStyle()}>
            {present.map((s, i) => {
              const logs   = logCount(s.id);
              const isLate = dayAtt[s.id]==="late";
              return (
                <div key={s.id} style={{ display:"flex", alignItems:"center", gap:14, padding:"13px 16px", borderBottom:i<present.length-1?`1px solid ${P.borderLight}`:"none" }}>
                  <div style={{ width:9, height:9, borderRadius:5, background:isLate?P.warning:P.accent, flexShrink:0 }}/>
                  <AvatarInitials name={studentName(s)} size={40} photo={s.photo}/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:600, color:P.text }}>{studentName(s)}</div>
                    <div style={{ fontSize:12, color:P.textMuted, marginTop:2 }}>
                      {isLate ? "Late arrival" : "Checked in"}
                      {logs > 0 && <span style={{ color:P.primary, fontWeight:600, marginLeft:6 }}>· {logs} update{logs!==1?"s":""}</span>}
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:8 }}>
                    <button onClick={() => onOpenLog(s,"photo")} style={{ width:HIT, height:HIT, borderRadius:HIT/2, border:`1.5px solid ${P.border}`, background:P.bg, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
                      <Icon name="camera" size={19} color={P.primary}/>
                    </button>
                    <button onClick={() => onOpenLog(s,null)} style={{ width:HIT, height:HIT, borderRadius:HIT/2, border:`1.5px solid ${P.border}`, background:P.bg, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
                      <Icon name="plus" size={19} color={P.accent}/>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Absent */}
      {absent.length > 0 && (
        <div style={{ padding:"20px 20px 0", opacity:0.6 }}>
          <div style={{ fontSize:11, fontWeight:700, color:P.textMuted, textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>
            Absent · {absent.length}
          </div>
          <div style={cardStyle()}>
            {absent.map((s, i) => (
              <div key={s.id} style={{ display:"flex", alignItems:"center", gap:14, padding:"12px 16px", borderBottom:i<absent.length-1?`1px solid ${P.borderLight}`:"none" }}>
                <div style={{ width:9, height:9, borderRadius:5, background:P.danger, flexShrink:0 }}/>
                <AvatarInitials name={studentName(s)} size={38} photo={s.photo}/>
                <div style={{ flex:1, fontSize:14, fontWeight:500, color:P.text }}>{studentName(s)}</div>
                <button onClick={() => setStatus(s.id,"present")} style={{ fontSize:12, fontWeight:600, color:P.textMuted, background:"transparent", border:`1px solid ${P.border}`, borderRadius:16, padding:"5px 12px", cursor:"pointer", fontFamily:"inherit" }}>Undo</button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ height:20 }}/>
    </div>
  );
});

// ─── LOG ──────────────────────────────────────────────────────────────────────
const LogTab = memo(({ students, activityLog, setActivityLog, today, currentUser, schoolId, isDemo, initStudent, initType, onClearInit }) => {
  // targets is a Set of student IDs, or ["class"] for whole class
  const [targets,  setTargets]  = useState(initStudent ? [String(initStudent.id)] : ["class"]);
  const [logType,  setLogType]  = useState(initType || "meal");
  const [detail,   setDetail]   = useState("");
  const [note,     setNote]     = useState("");
  const [photo,    setPhoto]    = useState(null);
  const [preview,  setPreview]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [showMore, setShowMore] = useState(false);
  const photoRef = useRef(null);

  useEffect(() => {
    if (initStudent) { setTargets([String(initStudent.id)]); onClearInit?.(); }
    if (initType)    { setLogType(initType); setShowMore(LOG_TILES_MORE.some(t=>t.id===initType)); }
  }, [initStudent?.id, initType]);

  const active       = students.filter(s => !s.enrollmentDate || s.enrollmentDate <= today);
  const isWholeClass = targets.includes("class");
  const targetList   = isWholeClass ? active : active.filter(s => targets.includes(String(s.id)));

  const toggleTarget = (id) => {
    if (id === "class") { setTargets(["class"]); return; }
    setTargets(prev => {
      const next = prev.filter(x => x !== "class"); // remove "class" if selecting individuals
      return next.includes(id) ? (next.filter(x=>x!==id).length ? next.filter(x=>x!==id) : ["class"]) : [...next, id];
    });
  };

  const handlePhoto = e => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5*1024*1024) { alert("Photo must be under 5MB"); return; }
    setPhoto(f);
    const r = new FileReader(); r.onload = ev => setPreview(ev.target.result); r.readAsDataURL(f);
    e.target.value = "";
  };

  const uploadPhoto = async f => {
    if (!f||!schoolId||isDemo) return null;
    try {
      const ext = f.name.split(".").pop()||"jpg";
      const path = `daily-logs/${schoolId}/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("school-assets").upload(path,f,{contentType:f.type});
      if (error) throw error;
      return supabase.storage.from("school-assets").getPublicUrl(path).data.publicUrl;
    } catch { return null; }
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    const photoUrl = photo ? await uploadPhoto(photo) : null;
    const now  = new Date();
    const time = `${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`;
    const entries = targetList.map(s => ({
      id:`${Date.now()}-${s.id}`, studentId:s.id,
      type:logType==="photo"?"note":logType,
      detail:logType==="photo"?"Photo":detail,
      note, time, timestamp:now.toISOString(),
      loggedBy:currentUser?.name||"Teacher",
      photoUrl: photoUrl || null,
    }));
    setActivityLog(prev => ({ ...prev, [today]:[...(prev[today]||[]),...entries] }));
    if (!isDemo&&schoolId) for (const e of entries) await insertActivityEntry({...e,schoolId,date:today}).catch(()=>{});
    setDetail(""); setNote(""); setPhoto(null); setPreview(null);
    setSaving(false); setSaved(true); setTimeout(()=>setSaved(false),2500);
  };

  const opts       = detailOpts(logType);
  const allTiles   = showMore ? [...LOG_TILES_PRIMARY,...LOG_TILES_MORE] : LOG_TILES_PRIMARY;
  const activeTile = [...LOG_TILES_PRIMARY,...LOG_TILES_MORE].find(t=>t.id===logType);
  const recent     = (activityLog[today]||[]).slice().reverse().slice(0,6);

  return (
    <div style={{ paddingBottom:80 }}>
      <div style={{ padding:"22px 20px 14px", borderBottom:`1px solid ${P.border}` }}>
        <div style={{ fontSize:26, fontWeight:800, color:P.navy, fontFamily:"'DM Serif Display',serif" }}>Log activity</div>
        <div style={{ fontSize:13, color:P.textMuted, marginTop:4 }}>Who is this for?</div>
      </div>

      {/* Target chips */}
      <div style={{ display:"flex", gap:8, padding:"14px 20px", overflowX:"auto", scrollbarWidth:"none", WebkitOverflowScrolling:"touch" }}>
        <Chip label="Whole class" selected={isWholeClass} onPress={()=>toggleTarget("class")} color={P.primary}/>
        {active.map(s => <Chip key={s.id} label={s.firstName} selected={targets.includes(String(s.id))} onPress={()=>toggleTarget(String(s.id))} color={P.primary}/>)}
      </div>

      <div style={{ height:1, background:P.borderLight }}/>

      {/* Log type grid — 3 cols matches mockup */}
      <div style={{ padding:"18px 20px 0" }}>
        <div style={{ fontSize:11, fontWeight:700, color:P.textMuted, textTransform:"uppercase", letterSpacing:1, marginBottom:12 }}>What happened?</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
          {allTiles.map(t => {
            const sel = logType===t.id;
            return (
              <button key={t.id} onClick={()=>{setLogType(t.id);setDetail("");}} style={{
                display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                gap:8,padding:"18px 8px",borderRadius:16,
                border:`2px solid ${sel?t.color:P.border}`,
                background:sel?t.color+"20":P.card,
                cursor:"pointer",fontFamily:"inherit",transition:"all .12s",
              }}>
                <div style={{ width:44,height:44,borderRadius:22,background:t.color+(sel?"40":"18"),display:"flex",alignItems:"center",justifyContent:"center" }}>
                  <Icon name={t.iconName} size={24} color={sel?t.color:t.color+"bb"}/>
                </div>
                <span style={{ fontSize:12,fontWeight:sel?700:500,color:sel?t.color:P.text }}>{t.label}</span>
              </button>
            );
          })}
          {/* More / Less toggle */}
          <button onClick={()=>setShowMore(v=>!v)} style={{
            display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
            gap:8,padding:"18px 8px",borderRadius:16,
            border:`2px solid ${P.border}`,background:P.card,
            cursor:"pointer",fontFamily:"inherit",
          }}>
            <div style={{ width:44,height:44,borderRadius:22,background:P.borderLight,display:"flex",alignItems:"center",justifyContent:"center" }}>
              <Icon name={showMore?"chevron-up":"plus"} size={22} color={P.textMuted}/>
            </div>
            <span style={{ fontSize:12,fontWeight:500,color:P.textMuted }}>{showMore?"Less":"More"}</span>
          </button>
        </div>
      </div>

      {/* Detail / photo */}
      <div style={{ padding:"20px" }}>
        {logType==="photo" ? (
          <>
            {preview ? (
              <div style={{ position:"relative",marginBottom:14 }}>
                <img src={preview} alt="" style={{ width:"100%",borderRadius:14,maxHeight:260,objectFit:"cover" }}/>
                <button onClick={()=>{setPhoto(null);setPreview(null);}} style={{ position:"absolute",top:10,right:10,width:30,height:30,borderRadius:15,background:"rgba(0,0,0,.55)",border:"none",color:"#fff",fontSize:18,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>×</button>
              </div>
            ) : (
              <button onClick={()=>photoRef.current?.click()} style={{ width:"100%",padding:"40px 0",borderRadius:14,border:`2px dashed ${P.border}`,background:P.bg,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:12,fontFamily:"inherit",marginBottom:14 }}>
                <Icon name="camera" size={34} color={P.textMuted}/>
                <span style={{ fontSize:14,color:P.textMuted }}>Tap to take or choose a photo</span>
              </button>
            )}
          </>
        ) : opts.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11,fontWeight:700,color:P.textMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:10 }}>Select</div>
            <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
              {opts.map(o => <Chip key={o} label={o} selected={detail===o} onPress={()=>setDetail(detail===o?"":o)} color={activeTile?.color||P.primary}/>)}
            </div>
            <div style={{ marginTop:14 }}/>
          </div>
        )}

        <textarea value={note} onChange={e=>setNote(e.target.value)} placeholder="Add a note... (optional)" rows={3}
          style={{ width:"100%",padding:"13px 14px",borderRadius:12,border:`1.5px solid ${P.border}`,fontSize:14,fontFamily:"inherit",outline:"none",boxSizing:"border-box",background:P.bg,color:P.text,resize:"none",marginBottom:14 }}/>

        {/* Optional photo for all log types except the dedicated photo tile */}
        {logType !== "photo" && (
          <div style={{ marginBottom:14 }}>
            <input ref={photoRef} type="file" accept="image/*" capture="environment" onChange={handlePhoto} style={{ display:"none" }}/>
            {preview ? (
              <div style={{ position:"relative",marginBottom:8 }}>
                <img src={preview} alt="" style={{ width:"100%",borderRadius:12,maxHeight:180,objectFit:"cover" }}/>
                <button onClick={()=>{setPhoto(null);setPreview(null);}} style={{ position:"absolute",top:8,right:8,width:28,height:28,borderRadius:14,background:"rgba(0,0,0,.55)",border:"none",color:"#fff",fontSize:17,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>×</button>
              </div>
            ) : (
              <button onClick={()=>photoRef.current?.click()} style={{ width:"100%",padding:"10px 14px",borderRadius:10,border:`1.5px dashed ${P.border}`,background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",gap:10,fontFamily:"inherit",color:P.textMuted,fontSize:13 }}>
                <Icon name="camera" size={18} color={P.textMuted}/>
                Add a photo (optional)
              </button>
            )}
          </div>
        )}

        <div style={{ fontSize:13,color:P.primary,fontWeight:600,background:P.primaryPale,padding:"10px 14px",borderRadius:10,marginBottom:14,display:"flex",alignItems:"center",gap:8 }}>
          <Icon name="send" size={14} color={P.primary}/>
          {isWholeClass ? `Sends to all ${active.length} parents in this room` : `Sends to ${targetList.length} student${targetList.length!==1?"s":""}'s parents`}
        </div>

        <button onClick={save} disabled={saving||(logType==="photo"&&!photo)} style={{
          width:"100%",padding:"17px 0",borderRadius:14,border:"none",
          background:saved?P.accent:P.primary,
          color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
          display:"flex",alignItems:"center",justifyContent:"center",gap:8,
          opacity:(saving||(logType==="photo"&&!photo))?0.55:1,transition:"background .2s",
        }}>
          {saved?<><Icon name="check" size={16} color="#fff"/> Sent!</>:saving?"Saving…":<><Icon name="send" size={16} color="#fff"/> Send update</>}
        </button>
      </div>

      {/* Today's feed */}
      {recent.length > 0 && (
        <div style={{ padding:"0 20px 20px" }}>
          <div style={{ fontSize:11,fontWeight:700,color:P.textMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:10 }}>Today's updates</div>
          <div style={cardStyle()}>
            {recent.map((entry,i) => {
              const s   = students.find(st=>st.id===entry.studentId);
              const typ = ACTIVITY_TYPES.find(t=>t.id===entry.type);
              return (
                <div key={entry.id||i} style={{ display:"flex",gap:12,padding:"12px 16px",borderBottom:i<recent.length-1?`1px solid ${P.borderLight}`:"none" }}>
                  <div style={{ width:34,height:34,borderRadius:17,background:(typ?.color||P.primary)+"22",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                    <Icon name={typ?.icon||"note"} size={16} color={typ?.color||P.primary}/>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13,fontWeight:600,color:P.text }}>{s?studentName(s):"Class"} · {entry.detail||typ?.label}</div>
                    {entry.note&&<div style={{ fontSize:12,color:P.textMuted,marginTop:2 }}>{entry.note}</div>}
                    {entry.photoUrl&&<img src={entry.photoUrl} alt="" style={{ marginTop:6,width:"100%",maxHeight:140,objectFit:"cover",borderRadius:8 }}/>}
                  </div>
                  <div style={{ fontSize:11,color:P.textMuted,whiteSpace:"nowrap" }}>{entry.time}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

// ─── MESSAGES ─────────────────────────────────────────────────────────────────
const MessagesTab = memo(({ channels, messages, setMessages, setChannels, currentUser, schoolId, isDemo, classroom, users }) => {
  const [activeId,    setActiveId]    = useState(null);
  const [msgText,     setMsgText]     = useState("");
  const [sending,     setSending]     = useState(false);
  const [showNewMsg,  setShowNewMsg]  = useState(false);
  const [dmSearch,    setDmSearch]    = useState("");
  const bottomRef = useRef(null);
  const loginTime = useRef(new Date().toISOString());

  const myChannels = useMemo(()=>
    (channels||[]).filter(c=>{
      const uid = String(currentUser?.id);
      const uname = (currentUser?.name||"").toLowerCase();
      const uemail = (currentUser?.email||"").toLowerCase();
      // Match by member ID, createdBy, or channel name containing teacher's name/email
      return (c.members||[]).map(String).includes(uid)
        || String(c.createdBy)===uid
        || (c.name||"").toLowerCase().includes(uname)
        || (c.name||"").toLowerCase().includes(uemail.split("@")[0]);
    }),
  [channels,currentUser]);

  const unreadFor = ch => {
    const stored = getLastRead(String(currentUser?.id), String(ch.id));
    // If never read before, use login time so historical messages don't show as unread
    const lr = stored || loginTime.current;
    return (messages||[]).filter(m =>
      String(m.channelId)===String(ch.id) &&
      (m.createdAt||`${m.date}T${m.time||"00:00"}`) > lr
    ).length;
  };
  const totalUnread = myChannels.reduce((n,c)=>n+unreadFor(c),0);
  const threadMsgs  = activeId ? (messages||[]).filter(m => String(m.channelId)===String(activeId)).sort((a,b)=>{ const ta=a.createdAt||`${a.date}T${a.time||"00:00"}`; const tb=b.createdAt||`${b.date}T${b.time||"00:00"}`; return new Date(ta)-new Date(tb); }) : [];
  useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:"smooth"}); },[threadMsgs.length]);

  // Mark channel as read whenever we open it or receive new messages while viewing
  useEffect(()=>{
    if (!activeId || !currentUser?.id) return;
    // Mark as read in localStorage whenever we view a thread
    setLastRead(String(currentUser?.id), String(activeId));
  },[activeId, threadMsgs.length]);

  const send = async () => {
    if (!msgText.trim()||!activeId||sending) return;
    setSending(true);
    const uid = String(currentUser?.id);
    const ch = (channels||[]).find(c=>String(c.id)===String(activeId));
    // If teacher is not in channel members (stale ID), add them now and persist
    if (ch && !(ch.members||[]).map(String).includes(uid)) {
      const updatedCh = {...ch, members:[...(ch.members||[]).map(String), uid]};
      if (setChannels) setChannels(prev=>(prev||[]).map(c=>String(c.id)===String(activeId)?updatedCh:c));
      if (!isDemo&&schoolId) upsertChannel(updatedCh, schoolId).catch(()=>{});
    }
    const now = new Date();
    const tempId = Date.now(); // numeric — matches App.jsx dupe-check: typeof m.id === "number"
    const msg = {
      id: tempId,
      channelId: String(activeId),
      text: msgText.trim(),
      senderId: uid,
      senderName: currentUser?.name,
      createdAt: now.toISOString(),
      date: now.toISOString().split("T")[0],
      time: now.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit",hour12:false}),
    };
    setMessages(prev => [...(prev||[]), msg]);
    setMsgText("");
    if (!isDemo && schoolId) {
      // Direct insert — bypass upsertMessage wrapper to get the raw error
      const { data: savedMsg, error: msgErr } = await supabase
        .from("messages")
        .insert({
          school_id: schoolId,
          channel_id: String(activeId),
          sender_id: String(currentUser?.id),
          text: msg.text,
          date: msg.date,
          time: msg.time,
        })
        .select()
        .single();
      if (msgErr) {
        console.error("MESSAGE INSERT ERROR:", JSON.stringify(msgErr));
        // Show error to user
        alert(`Message failed: ${msgErr.message || msgErr.code || JSON.stringify(msgErr)}`);
      } else if (savedMsg) {
        // Replace temp ID with real DB ID
        setMessages(prev => (prev||[]).map(m => m.id === tempId ? {...m, id: savedMsg.id, createdAt: savedMsg.created_at} : m));
      }
    }
    setSending(false);
  };

  const startDM = (otherUser) => {
    const uid = String(currentUser?.id);
    const oid = String(otherUser.id);
    const existing = (channels||[]).find(c => c.type==="dm" && (c.members||[]).map(String).includes(uid) && (c.members||[]).map(String).includes(oid));
    if (existing) { setActiveId(existing.id); setShowNewMsg(false); return; }
    const dmId = `dm-${[uid,oid].sort().join("-")}`;
    const newCh = { id:dmId, name:otherUser.name, type:"dm", icon:"messages", description:"", members:[uid,oid], createdBy:uid };
    if (setChannels) setChannels(prev => [...(prev||[]), newCh]);
    if (!isDemo && schoolId) upsertChannel(newCh, schoolId).then(saved => {
      const realId = saved?.id || dmId;
      if (setChannels) setChannels(prev => prev.map(c => c.id===dmId ? {...c,id:realId} : c));
      setActiveId(realId);
    }).catch(()=>setActiveId(dmId));
    else setActiveId(dmId);
    setShowNewMsg(false); setDmSearch("");
  };

  const chLabel = ch => {
    // For DMs, always show the OTHER person's name regardless of ch.name
    if (ch.type === "dm") {
      const oid = (ch.members||[]).find(id => String(id) !== String(currentUser?.id));
      const other = (users||[]).find(u => String(u.id)===String(oid));
      if (other) return other.name;
    }
    return ch.name || "Conversation";
  };

  const chLabelFull = ch => {
    // For thread header — "Conversation with [name]"
    if (ch.type === "dm") {
      const oid = (ch.members||[]).find(id => String(id) !== String(currentUser?.id));
      const other = (users||[]).find(u => String(u.id)===String(oid));
      if (other) return `Conversation with ${other.name}`;
    }
    return ch.name || "Conversation";
  };
  const chPreview = ch => (messages||[]).filter(m=>m.channelId===ch.id).slice(-1)[0]?.text?.slice(0,50)||"No messages yet";
  const chTime    = ch => { const last=(messages||[]).filter(m=>m.channelId===ch.id).slice(-1)[0]; if(!last?.createdAt)return""; const d=new Date(last.createdAt),now=new Date(); return d.toDateString()===now.toDateString()?d.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"}):d.toLocaleDateString("en-US",{month:"short",day:"numeric"}); };

  if (activeId) {
    const ch = myChannels.find(c=>c.id===activeId);
    return (
      <div style={{ display:"flex",flexDirection:"column",height:"calc(100vh - 72px)" }}>
        <div style={{ display:"flex",alignItems:"center",gap:12,padding:"14px 20px",borderBottom:`1px solid ${P.border}`,background:P.card,flexShrink:0 }}>
          <button onClick={()=>setActiveId(null)} style={{ background:"none",border:"none",cursor:"pointer",color:P.primary,display:"flex",alignItems:"center",gap:4,fontSize:14,fontWeight:700,fontFamily:"inherit",padding:0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            Back
          </button>
          <div style={{ flex:1,fontSize:15,fontWeight:700,color:P.text }}>{ch?chLabelFull(ch):""}</div>
        </div>
        <div style={{ flex:1,overflowY:"auto",padding:"16px 20px",display:"flex",flexDirection:"column",gap:10 }}>
          {threadMsgs.length===0&&<div style={{ textAlign:"center",color:P.textMuted,fontSize:13,marginTop:40 }}>No messages yet. Say hello!</div>}
          {threadMsgs.map((m,i)=>{
            const isMe = String(m.senderId)===String(currentUser?.id);
            // Resolve sender name from users list by ID — senderName field is not persisted to DB
            const senderUser = (users||[]).find(u => String(u.id)===String(m.senderId));
            const resolvedSenderName = senderUser?.name || m.senderName || `ID:${m.senderId}`;
            return (
              <div key={m.id||i} style={{ display:"flex",justifyContent:isMe?"flex-end":"flex-start",flexDirection:"column",alignItems:isMe?"flex-end":"flex-start" }}>
                {!isMe && (
                  <div style={{ fontSize:11,color:P.textMuted,marginBottom:3,paddingLeft:4 }}>{resolvedSenderName}</div>
                )}
                <div style={{ maxWidth:"76%",padding:"11px 15px",borderRadius:isMe?"18px 18px 4px 18px":"18px 18px 18px 4px",background:isMe?P.primary:P.card,border:`1px solid ${isMe?P.primary:P.border}` }}>
                  <div style={{ fontSize:14,color:isMe?"#fff":P.text,lineHeight:1.45 }}>{m.text}</div>
                  <div style={{ fontSize:10,color:isMe?"rgba(255,255,255,.55)":P.textMuted,marginTop:3,textAlign:"right" }}>{new Date(m.createdAt||`${m.date}T${m.time||"00:00"}`).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})}</div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef}/>
        </div>
        <div style={{ display:"flex",gap:10,padding:"12px 20px",borderTop:`1px solid ${P.border}`,background:P.card,flexShrink:0 }}>
          <input value={msgText} onChange={e=>setMsgText(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&send()} placeholder="Message…"
            style={{ flex:1,padding:"12px 16px",borderRadius:24,border:`1.5px solid ${P.border}`,fontSize:14,fontFamily:"inherit",background:P.bg,color:P.text,outline:"none" }}/>
          <button onClick={send} disabled={!msgText.trim()||sending} style={{ width:HIT,height:HIT,borderRadius:HIT/2,background:P.primary,border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",opacity:!msgText.trim()?0.5:1 }}>
            <Icon name="send" size={18} color="#fff"/>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom:80 }}>
      <div style={{ padding:"22px 20px 14px",borderBottom:`1px solid ${P.border}` }}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
          <div style={{ fontSize:26,fontWeight:800,color:P.navy,fontFamily:"'DM Serif Display',serif" }}>Messages</div>
          <div style={{ display:"flex",gap:8,alignItems:"center" }}>
            {totalUnread>0&&<span style={{ background:P.danger,color:"#fff",fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20 }}>{totalUnread} new</span>}
            <button onClick={()=>setShowNewMsg(true)} style={{ width:36,height:36,borderRadius:18,background:P.primary,border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>
              <Icon name="plus" size={18} color="#fff"/>
            </button>
          </div>
        </div>
        <div style={{ fontSize:13,color:P.textMuted,marginTop:4 }}>{classroom?.name||"Your room"}</div>
      </div>

      {showNewMsg && (
        <div style={{ padding:"16px 20px" }}>
          <div style={{ fontSize:13,fontWeight:700,color:P.textMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:12 }}>Start a conversation</div>
          <input value={dmSearch} onChange={e=>setDmSearch(e.target.value)} placeholder="Search name..." style={{ width:"100%",padding:"11px 14px",borderRadius:10,border:`1px solid ${P.border}`,background:P.cardAlt,color:P.text,fontSize:14,fontFamily:"inherit",outline:"none",boxSizing:"border-box",marginBottom:12 }}/>
          <div style={{ ...cardStyle() }}>
            {(users||[]).filter(u=>String(u.id)!==String(currentUser?.id)&&(u.name||"").toLowerCase().includes(dmSearch.toLowerCase())).slice(0,20).map((u,i,arr)=>(
              <div key={u.id} onClick={()=>startDM(u)} style={{ display:"flex",alignItems:"center",gap:12,padding:"14px 16px",borderBottom:i<arr.length-1?`1px solid ${P.borderLight}`:"none",cursor:"pointer" }}>
                <div style={{ width:40,height:40,borderRadius:20,background:P.primaryPale,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:700,color:P.primary,flexShrink:0 }}>{u.name?.[0]?.toUpperCase()||"?"}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14,fontWeight:600,color:P.text }}>{u.name}</div>
                  <div style={{ fontSize:12,color:P.textMuted,marginTop:1 }}>{u.role||"Staff"}</div>
                </div>
              </div>
            ))}
            {(users||[]).filter(u=>String(u.id)!==String(currentUser?.id)&&(u.name||"").toLowerCase().includes(dmSearch.toLowerCase())).length===0&&(
              <div style={{ padding:"20px",textAlign:"center",color:P.textMuted,fontSize:13 }}>No users found</div>
            )}
          </div>
          <button onClick={()=>{setShowNewMsg(false);setDmSearch("");}} style={{ width:"100%",marginTop:12,padding:"12px",borderRadius:10,border:`1px solid ${P.border}`,background:"transparent",color:P.textMuted,fontSize:14,fontFamily:"inherit",cursor:"pointer" }}>Cancel</button>
        </div>
      )}
      {!showNewMsg && myChannels.length===0?(
        <div style={{ textAlign:"center",color:P.textMuted,fontSize:14,padding:"60px 20px",lineHeight:1.7 }}>
          No conversations yet.<br/>Tap + to start a conversation.
        </div>
      ):(
        <div style={{ padding:"16px 20px" }}>
          <div style={cardStyle()}>
            {myChannels.map((ch,i)=>{
              const unread=unreadFor(ch);
              return (
                <div key={ch.id} onClick={()=>{
                  setActiveId(ch.id);
                  // Mark as read in localStorage — survives polls and page switches
                  setLastRead(String(currentUser?.id), String(ch.id));
                }} style={{ display:"flex",alignItems:"center",gap:14,padding:"15px 16px",borderBottom:i<myChannels.length-1?`1px solid ${P.borderLight}`:"none",background:unread>0?P.primaryPale:"transparent",cursor:"pointer" }}>
                  <div style={{ width:46,height:46,borderRadius:23,background:P.primaryPale,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontWeight:700,color:P.primary,flexShrink:0 }}>
                    {chLabel(ch)[0]?.toUpperCase()||"?"}
                  </div>
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:2 }}>
                      <div style={{ fontSize:14,fontWeight:unread>0?700:500,color:P.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{chLabel(ch)}</div>
                      {unread>0&&<div style={{ width:8,height:8,borderRadius:4,background:P.primary,flexShrink:0 }}/>}
                    </div>
                    <div style={{ fontSize:12,color:P.textMuted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{chPreview(ch)}</div>
                  </div>
                  <div style={{ fontSize:11,color:P.textMuted,whiteSpace:"nowrap" }}>{chTime(ch)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

// ─── MORE ─────────────────────────────────────────────────────────────────────
const MoreTab = memo(({ currentUser, onShowSettings, onLogout, portalProps }) => {
  const [subPage, setSubPage] = useState(null);

  const items = [
    { label:"Attendance",    id:"attendance",    sub:"Take & view room attendance", iconName:"attendance", color:"#4CAF7D" },
    { label:"My attendance", id:"my-attendance", sub:"Log your own attendance",     iconName:"calendar",   color:"#8B7E6A" },
    { label:"Events",        id:"events",        sub:"Upcoming school events",       iconName:"events",     color:"#378ADD" },
    { label:"Tickets",       id:"tickets",       sub:"Submit or view issues",        iconName:"tickets",    color:"#E8852E" },
    { label:"Students",      id:"students",      sub:"Student profiles",              iconName:"students",   color:"#7C4DFF" },
    { label:"Reports",       id:"reports",       sub:"Attendance & activity",         iconName:"reports",    color:"#E91E63" },
    { label:"Classrooms",    id:"classrooms",    sub:"Your classroom info",           iconName:"classrooms", color:"#00BCD4" },
  ];

  // Sub-page wrapper — renders the actual page inside the portal shell
  if (subPage) {
    const item = items.find(i=>i.id===subPage);
    const { students, classrooms, teachers, tickets, setTickets, events, setEvents,
            studentAttendance, setStudentAttendance, teacherAttendance, setTeacherAttendance,
            activityLog, currentUser:cu, schoolId, isDemo,
            onSubmitAttendance, attendanceSubmissions } = portalProps;
    return (
      <div style={{ paddingBottom:80 }}>
        {/* Back header */}
        <div style={{ display:"flex",alignItems:"center",gap:12,padding:"14px 20px",borderBottom:`1px solid ${P.border}`,background:P.card }}>
          <button onClick={()=>setSubPage(null)} style={{ background:"none",border:"none",cursor:"pointer",color:P.primary,display:"flex",alignItems:"center",gap:4,fontSize:14,fontWeight:700,fontFamily:"inherit",padding:0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            Back
          </button>
          <div style={{ flex:1,fontSize:15,fontWeight:700,color:P.text }}>{item?.label}</div>
        </div>
        <div style={{ background:"#fff", minHeight:"calc(100vh - 130px)" }}>
          {subPage==="attendance"    && <AttendancePage classrooms={classrooms.filter(c=>c.id===cu?.assignedRoom)} students={students} teachers={[]} studentAttendance={studentAttendance} setStudentAttendance={setStudentAttendance} teacherAttendance={teacherAttendance||{}} setTeacherAttendance={setTeacherAttendance} currentUser={cu} schoolId={schoolId} attendanceSubmissions={attendanceSubmissions||[]} excludeWeekends={true} schoolTodayStr={()=>new Date().toISOString().split("T")[0]} onSubmitAttendance={onSubmitAttendance||(() => {})} onRefresh={()=>{}}/>}
          {subPage==="my-attendance" && <MyAttendancePage currentUser={cu} schoolId={schoolId} teacherAttendance={teacherAttendance} setTeacherAttendance={setTeacherAttendance}/>}
          {subPage==="events"        && <EventsPage events={events} setEvents={setEvents} currentUser={cu}/>}
          {subPage==="tickets"       && <TicketsPage tickets={tickets?.filter(t=>t.createdBy===cu?.id||t.assignedTo===cu?.id)||[]} setTickets={setTickets} currentUser={cu} users={teachers} teachers={teachers}/>}
          {subPage==="students"      && <StudentsPage students={students} setStudents={null} classrooms={classrooms.filter(c=>c.id===cu?.assignedRoom)} currentUser={cu} schoolYears={[]} activeYearId={""} season={"spring"} tuitionPlans={[]} schoolId={schoolId}/>}
          {subPage==="reports"       && <ReportsPage students={students} classrooms={classrooms.filter(c=>c.id===cu?.assignedRoom)} teachers={teachers.filter(t=>t.assignedRoom===cu?.assignedRoom)} studentAttendance={studentAttendance} teacherAttendance={teacherAttendance||{}} tickets={[]} events={events||[]} activityLog={activityLog||{}} dailyAssignments={{}} setPage={()=>{}} setStudentSearchInit={()=>{}} currentUser={cu} attendanceSubmissions={[]} schoolSettings={{}}/>}
          {subPage==="classrooms"    && <ClassroomsPage classrooms={classrooms.filter(c=>c.id===cu?.assignedRoom)} setClassrooms={null} teachers={teachers.filter(t=>t.assignedRoom===cu?.assignedRoom)} students={students} currentUser={cu} onStudentClick={()=>{}} tuitionPlans={[]}/>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom:80 }}>
      <div style={{ padding:"22px 20px 14px",borderBottom:`1px solid ${P.border}` }}>
        <div style={{ fontSize:26,fontWeight:800,color:P.navy,fontFamily:"'DM Serif Display',serif" }}>More</div>
        <div style={{ fontSize:13,color:P.textMuted,marginTop:4 }}>Additional features</div>
      </div>
      <div style={{ padding:"16px 20px" }}>
        <div style={cardStyle()}>
          {items.map((item,i)=>(
            <button key={item.id} onClick={()=>setSubPage(item.id)} style={{ width:"100%",display:"flex",alignItems:"center",gap:16,padding:"16px 16px",borderBottom:i<items.length-1?`1px solid ${P.borderLight}`:"none",background:"transparent",border:"none",borderRadius:0,cursor:"pointer",textAlign:"left",fontFamily:"inherit" }}>
              <div style={{ width:48,height:48,borderRadius:14,background:item.color+"22",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                <Icon name={item.iconName} size={24} color={item.color}/>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:15,fontWeight:600,color:P.text }}>{item.label}</div>
                <div style={{ fontSize:12,color:P.textMuted,marginTop:2 }}>{item.sub}</div>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={P.textMuted} strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          ))}
        </div>

        {/* Appearance */}
        <div style={{ marginTop:16 }}>
          <button onClick={onShowSettings} style={{ width:"100%",display:"flex",alignItems:"center",gap:16,padding:"16px",background:P.card,border:`1px solid ${P.border}`,borderRadius:14,cursor:"pointer",textAlign:"left",fontFamily:"inherit" }}>
            <div style={{ width:48,height:48,borderRadius:14,background:"rgba(255,255,255,0.08)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
              <Icon name="eye" size={24} color={P.textMuted}/>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:15,fontWeight:600,color:P.text }}>Appearance</div>
              <div style={{ fontSize:12,color:P.textMuted,marginTop:2 }}>Change theme & dark mode</div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={P.textMuted} strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>

        <div style={{ marginTop:20,marginBottom:8,fontSize:11,fontWeight:700,color:P.textMuted,textTransform:"uppercase",letterSpacing:1 }}>Signed in as</div>
        <div style={{ ...cardStyle(), padding:"16px" }}>
          <div style={{ display:"flex",alignItems:"center",gap:14 }}>
            <div style={{ width:48,height:48,borderRadius:24,background:P.primaryPale,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,fontWeight:700,color:P.primary,flexShrink:0 }}>
              {currentUser?.name?.[0]?.toUpperCase()||"T"}
            </div>
            <div>
              <div style={{ fontSize:15,fontWeight:600,color:P.text }}>{currentUser?.name||"Teacher"}</div>
              <div style={{ fontSize:12,color:P.textMuted,marginTop:2 }}>{currentUser?.email||""}</div>
            </div>
          </div>
        </div>

        <button onClick={onLogout} style={{ width:"100%",display:"flex",alignItems:"center",gap:16,padding:"16px",background:P.card,border:`1px solid ${P.border}`,borderRadius:14,cursor:"pointer",textAlign:"left",fontFamily:"inherit",marginTop:10 }}>
          <div style={{ width:48,height:48,borderRadius:14,background:"rgba(239,83,80,0.15)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
            <Icon name="logout" size={22} color={P.danger}/>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:15,fontWeight:600,color:P.danger }}>Sign out</div>
            <div style={{ fontSize:12,color:P.textMuted,marginTop:2 }}>Log out of ShrewCare</div>
          </div>
        </button>
      </div>
    </div>
  );
});

// ─── ROOT ─────────────────────────────────────────────────────────────────────
const TeacherPortalPage = memo(({
  students, classrooms, teachers,
  activityLog, setActivityLog,
  studentAttendance, setStudentAttendance,
  teacherAttendance, setTeacherAttendance,
  tickets, setTickets, events, setEvents,
  messages, setMessages, channels, setChannels,
  currentUser, setPage, schoolId, isDemo, schoolTodayStr, onShowSettings, onLogout, season, darkMode, users,
  onSubmitAttendance, attendanceSubmissions,
}) => {
  const today    = schoolTodayStr ? schoolTodayStr() : todayStr();
  // Combine all users for messaging (teachers + users list)
  const allUsersForPortal = useMemo(()=>{
    const base = [...(users||[]),...(teachers||[])];
    const seen = new Set();
    return base.filter(u=>{ const k=String(u.id); if(seen.has(k))return false; seen.add(k); return true; });
  },[users,teachers]);
  // Sync primary color from app theme so appearance changes take effect
  const themePrimary = COLORS.primary || P.primary;
  const [tab,    setTab]    = useState("today");
  const [initStu,setInitStu]= useState(null);
  const [initTyp,setInitTyp]= useState(null);

  const classroom = useMemo(()=>{
    if (currentUser?.assignedRoom)
      return classrooms.find(c=>String(c.id)===String(currentUser.assignedRoom));
    return null; // null = not assigned yet
  },[classrooms,currentUser]);
  // Only show students in the assigned classroom
  const myStudents = useMemo(()=>{
    if (!classroom) return [];
    return students.filter(s => String(s.classroomId) === String(classroom.id));
  },[students, classroom]);

  const portalLoginTime = useRef(new Date().toISOString());
  const totalUnread = useMemo(()=>{
    const uid = String(currentUser?.id);
    return (channels||[])
      .filter(c=>(c.members||[]).map(String).includes(uid)||String(c.createdBy)===uid)
      .reduce((n,ch)=>{
        const stored = getLastRead(uid, String(ch.id));
        const lr = stored || portalLoginTime.current;
        return n + (messages||[]).filter(m =>
          String(m.channelId)===String(ch.id) &&
          (m.createdAt||`${m.date}T${m.time||"00:00"}`) > lr
        ).length;
      }, 0);
  },[channels,messages,currentUser]);

  const openLog = (student, type) => { setInitStu(student); setInitTyp(type); setTab("log"); };

  const TABS = [
    { id:"today",    label:"Today"    },
    { id:"log",      label:"Log"      },
    { id:"messages", label:"Messages", badge:totalUnread },
    { id:"more",     label:"More"     },
  ];

  // Re-read COLORS on every render so theme changes propagate into P proxy
  const _primary = COLORS.primary; // eslint-disable-line no-unused-vars

  return (
    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", background:P.bg, minHeight:"100vh", paddingBottom:"env(safe-area-inset-bottom,0)" }}>
      {tab==="today" && !classroom && (
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"60vh", padding:"40px 24px", textAlign:"center" }}>
          <div style={{ width:64, height:64, borderRadius:32, background:P.primaryPale, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:20 }}>
            <Icon name="classrooms" size={30} color={P.primary}/>
          </div>
          <div style={{ fontSize:20, fontWeight:700, color:P.text, marginBottom:8 }}>No classroom assigned</div>
          <div style={{ fontSize:14, color:P.textMuted, lineHeight:1.6, maxWidth:280 }}>
            Your account hasn't been assigned to a classroom yet. Please ask your director to assign you a classroom in the Teachers settings.
          </div>
        </div>
      )}
      {tab==="today" && classroom && <TodayTab students={myStudents} classroom={classroom} studentAttendance={studentAttendance} setStudentAttendance={setStudentAttendance} activityLog={activityLog} today={today} onOpenLog={openLog} schoolId={schoolId} isDemo={isDemo} currentUser={currentUser}/>}
      {tab==="log"      && <LogTab      students={myStudents} activityLog={activityLog} setActivityLog={setActivityLog} today={today} currentUser={currentUser} schoolId={schoolId} isDemo={isDemo} initStudent={initStu} initType={initTyp} onClearInit={()=>{setInitStu(null);setInitTyp(null);}}/>}
      {tab==="messages" && <MessagesTab channels={channels} messages={messages} setMessages={setMessages} setChannels={setChannels} currentUser={currentUser} schoolId={schoolId} isDemo={isDemo} classroom={classroom} users={allUsersForPortal}/>}
      {tab==="more" && <MoreTab
        currentUser={currentUser}
        onShowSettings={onShowSettings||(() => {})}
        onLogout={onLogout||(() => {})}
        portalProps={{ students:myStudents, classrooms, teachers, tickets, setTickets, events, setEvents,
          studentAttendance, setStudentAttendance, teacherAttendance, setTeacherAttendance,
          activityLog, currentUser, schoolId, isDemo,
          onSubmitAttendance, attendanceSubmissions: attendanceSubmissions||[] }}
      />}

      {/* Bottom nav — fixed, safe area aware, always on top */}
      <div style={{ position:"fixed",bottom:0,left:0,right:0,display:"flex",background:P.card,borderTop:`1px solid ${P.border}`,zIndex:9999,paddingBottom:"env(safe-area-inset-bottom,0px)",WebkitBackfaceVisibility:"hidden",transform:"translateZ(0)" }}>
        {TABS.map(({ id, label, badge }) => {
          const active = tab===id;
          const c = active ? P.primary : P.textMuted;
          return (
            <button key={id} onClick={()=>setTab(id)} style={{ flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"10px 0 8px",gap:3,border:"none",background:"transparent",cursor:"pointer",fontSize:11,fontWeight:active?700:400,color:c,fontFamily:"inherit",minHeight:56 }}>
              <div style={{ position:"relative" }}>
                <NavIcon id={id} color={c}/>
                {badge>0&&<div style={{ position:"absolute",top:-4,right:-5,minWidth:16,height:16,borderRadius:8,background:P.danger,color:"#fff",fontSize:9,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 3px" }}>{badge>9?"9+":badge}</div>}
              </div>
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
});

export default TeacherPortalPage;
